"use client";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Search } from "lucide-react";
import { useI18N, useAccessibilityTags, useAuth, useSavedFilters, useZoomDetection } from "common/hooks";
import { cn } from "@/lib/utils";
import Checkbox from "@/components/common/Checkbox";
import { StandardButton } from "@/components/common/StandardButton";
import Switch from "@/components/common/Switch";
import AnalyticsService from "@/services/analytics";
import { useAtom } from "jotai";
import { savedFiltersAppliedAtom } from "@/store/atoms";
import FocusTrap from "focus-trap-react";
import ScrollContainer from "react-indiana-drag-scroll";

// ============================================================================
// Types
// ============================================================================

export interface AccessibilityFilterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Currently selected option IDs (number[]) */
  selectedOptions: number[];
  /** Callback when options change */
  onOptionsChange: (optionIds: number[]) => void;
  /** Callback when clear all is clicked */
  onClearAll: () => void;
  /** Custom className for positioning */
  className?: string;
  /** Optional external accessibility tags data (overrides hook data if provided) */
  accessibilityTagsData?: any[];
}

// ============================================================================
// Component
// ============================================================================

export function AccessibilityFilterPanel({
  isOpen,
  onClose,
  selectedOptions,
  onOptionsChange,
  onClearAll,
  className,
  accessibilityTagsData,
}: AccessibilityFilterPanelProps) {
  const i18n = useI18N();
  const hookData = useAccessibilityTags();
  const { isLoggedIn } = useAuth();
  const { savedFilterIds, loadSavedFilters } = useSavedFilters();
  const [isSavedFiltersApplied, setIsSavedFiltersApplied] = useAtom(
    savedFiltersAppliedAtom
  );

  // Use external data if provided, otherwise use hook data
  const accessibilityTags =
    accessibilityTagsData !== undefined
      ? accessibilityTagsData
      : hookData.accessibilityTags;

  // Prepare data manually if using external data (wrapped in useMemo to avoid recreating on every render)
  const { preparedData, tagsToAdd } = useMemo(() => {
    if (accessibilityTagsData === undefined) {
      return {
        preparedData: hookData.preparedData,
        tagsToAdd: hookData.tagsToAdd,
      };
    }

    const tagsToAddArray: any[] = [];
    const ids = [964, 844, 553, 554, 753];
    const idstoAdd: any[] = [];

    accessibilityTags.forEach((option: any) => {
      option.accessibility_tags?.forEach((o: any) => {
        if (ids.includes(o.id)) {
          tagsToAddArray.push(o);
          idstoAdd.push(o.id);
        }
      });
    });

    const prepared = accessibilityTags.map((option: any) => {
      const filteredTags =
        option.accessibility_tags?.filter(
          (o: any) => !idstoAdd.includes(o.id),
        ) || [];
      return { ...option, accessibility_tags: filteredTags };
    });

    return {
      preparedData: prepared,
      tagsToAdd: tagsToAddArray,
    };
  }, [
    accessibilityTagsData,
    accessibilityTags,
    hookData.preparedData,
    hookData.tagsToAdd,
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [allSubItemsSelected, setAllSubItemsSelected] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [liveMessage, setLiveMessage] = useState("");

  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const categoryHeadingRefs = useRef<Record<string, HTMLHeadingElement | null>>({});
  const categoryChipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isProgrammaticScroll = useRef(false);

  const isZoomedIn = useZoomDetection(1.25);

  // Load saved filters when panel opens
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      loadSavedFilters();
    }
  }, [isOpen, isLoggedIn, loadSavedFilters]);

  // Set first category as selected by default when data is loaded
  useEffect(() => {
    if (preparedData.length > 0 && !selectedCategory) {
      const firstCategoryWithTags = preparedData.find(
        (category: any) => category.accessibility_tags.length > 0,
      );
      if (firstCategoryWithTags) {
        setSelectedCategory(firstCategoryWithTags.name);
      }
    }
  }, [preparedData, selectedCategory]);

  // Set last category as expanded by default when data is loaded
  useEffect(() => {
    if (preparedData.length > 0 && expandedCategories.size === 0) {
      const categoriesWithTags = preparedData.filter(
        (category: any) => category.accessibility_tags.length > 0,
      );
      if (categoriesWithTags.length > 0) {
        const lastCategory = categoriesWithTags[categoriesWithTags.length - 1];
        setExpandedCategories(new Set([lastCategory.name]));
      }
    }
  }, [preparedData, expandedCategories.size]);

  // Check if all sub-items are selected for tag 428
  useEffect(() => {
    if (selectedOptions.includes(428)) {
      setAllSubItemsSelected(
        tagsToAdd.every((tag: any) => selectedOptions.includes(tag.id)),
      );
    } else {
      setAllSubItemsSelected(false);
    }
  }, [tagsToAdd, selectedOptions]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Scroll to category section when category chip is clicked
  const handleCategoryClick = useCallback((categoryName: string) => {
    setSelectedCategory(categoryName);
    isProgrammaticScroll.current = true;
    
    const sectionEl = sectionRefs.current[categoryName];
    const headingEl = categoryHeadingRefs.current[categoryName];
    
    if (sectionEl && contentRef.current) {
      const containerTop = contentRef.current.getBoundingClientRect().top;
      const sectionTop = sectionEl.getBoundingClientRect().top;
      const offset = sectionTop - containerTop;
      contentRef.current.scrollTo({
        top: contentRef.current.scrollTop + offset - 8,
        behavior: "smooth",
      });
      
      // Move screen reader focus to the category heading
      setTimeout(() => {
        if (headingEl) {
          headingEl.focus();
        }
        // Reset programmatic scroll flag after scroll completes
        isProgrammaticScroll.current = false;
      }, 1000);
    }
  }, []);

  // Filter tags based on search term
  const filterTags = useCallback((tags: any[]) => {
    if (!searchTerm) return tags;
    return tags.filter((tag: any) =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm]);

  // Get filtered categories with their tags
  const filteredCategories = useMemo(() => {
    return preparedData
      .map((category: any) => ({
        ...category,
        accessibility_tags: filterTags(category.accessibility_tags),
      }))
      .filter((category: any) => category.accessibility_tags.length > 0);
  }, [preparedData, filterTags]);

  // Scroll spy - auto-select category chip when scrolling through sections
  useEffect(() => {
    if (!contentRef.current || filteredCategories.length === 0) return;

    const scrollContainer = contentRef.current;
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!scrollContainer) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        
        let mostVisibleCategory = "";
        let maxVisibleArea = 0;

        // Check each category section to find which has most visible content
        filteredCategories.forEach((category: any) => {
          if (category.accessibility_tags.length <= 0) return;
          
          const sectionEl = sectionRefs.current[category.name];
          if (sectionEl) {
            const rect = sectionEl.getBoundingClientRect();
            
            // Calculate visible area within the scroll container
            const visibleTop = Math.max(rect.top, containerTop);
            const visibleBottom = Math.min(rect.bottom, containerTop + containerHeight);
            const visibleArea = Math.max(0, visibleBottom - visibleTop);
            
            if (visibleArea > maxVisibleArea) {
              maxVisibleArea = visibleArea;
              mostVisibleCategory = category.name;
            }
          }
        });

        // Update selected category if different
        if (mostVisibleCategory && mostVisibleCategory !== selectedCategory) {
          setSelectedCategory(mostVisibleCategory);
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [filteredCategories, selectedCategory]);

  // Handle keyboard navigation for category chips
  const handleCategoryChipKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number, totalChips: number) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = (index + 1) % totalChips;
        categoryChipRefs.current[nextIndex]?.focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex = (index - 1 + totalChips) % totalChips;
        categoryChipRefs.current[prevIndex]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        categoryChipRefs.current[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        categoryChipRefs.current[totalChips - 1]?.focus();
      }
    },
    [],
  );

  // Toggle option selection
  const handleOptionToggle = useCallback(
    (optionId: number, optionName?: string) => {
      const isSelected = selectedOptions.includes(optionId);
      const newCount = isSelected ? selectedOptions.length - 1 : selectedOptions.length + 1;
      const filterWord = newCount === 1 ? "filter" : "filters";
      const label = optionName || String(optionId);

      setLiveMessage(
        isSelected
          ? `${label} removed. ${newCount} ${filterWord} active.`
          : `${label} selected. ${newCount} ${filterWord} active.`,
      );

      // Track analytics
      AnalyticsService.trackGAEventHandler("searchAccessibilityOptionToggled");
      AnalyticsService.trackMx("searchAccessibilityOptionToggled", {
        OptionId: optionId,
        OptionName: optionName || "",
        IsSelected: !isSelected,
      });

      if (!isSelected) {
        onOptionsChange([...selectedOptions, optionId]);
      } else {
        // Special handling for tag 428 - uncheck all sub-items too
        if (optionId === 428) {
          const ids = [428, 964, 844, 553, 554, 753];
          setAllSubItemsSelected(false);
          onOptionsChange(selectedOptions.filter((o) => !ids.includes(o)));
        } else {
          onOptionsChange(selectedOptions.filter((o) => o !== optionId));
        }
      }
    },
    [selectedOptions, onOptionsChange],
  );

  // Handle"All" sub-items toggle
  const handleAllSubItemsToggle = useCallback(() => {
    if (allSubItemsSelected) {
      // Remove all sub-items
      onOptionsChange(
        selectedOptions.filter(
          (o) => !tagsToAdd.map((t: any) => t.id).includes(o),
        ),
      );
    } else {
      // Add all sub-items
      onOptionsChange([
        ...selectedOptions,
        ...tagsToAdd
          .map((t: any) => t.id)
          .filter((id: number) => !selectedOptions.includes(id)),
      ]);
    }
    setAllSubItemsSelected(!allSubItemsSelected);
  }, [allSubItemsSelected, selectedOptions, tagsToAdd, onOptionsChange]);

  // Handle individual sub-item toggle
  const handleSubItemToggle = useCallback(
    (tagId: number) => {
      if (selectedOptions.includes(tagId)) {
        setAllSubItemsSelected(false);
        onOptionsChange(selectedOptions.filter((o) => o !== tagId));
      } else {
        const newOptions = [...selectedOptions, tagId];
        onOptionsChange(newOptions);
        // Check if all sub-items are now selected
        if (tagsToAdd.every((t: any) => newOptions.includes(t.id))) {
          setAllSubItemsSelected(true);
        }
      }
    },
    [selectedOptions, tagsToAdd, onOptionsChange],
  );

  // Apply saved filters
  const applySavedFilters = useCallback(
    (apply: boolean) => {
      setIsSavedFiltersApplied(apply);

      if (savedFilterIds) {
        if (apply) {
          AnalyticsService.trackGAEventHandler("filtersAppliedSavedFiltersOn");
          AnalyticsService.trackMx("filtersAppliedSavedFiltersOn", {});

          // Add saved filter IDs to selected options
          const newOptions = [...selectedOptions];
          savedFilterIds.forEach((id: number) => {
            if (!newOptions.includes(id)) {
              newOptions.push(id);
            }
          });
          onOptionsChange(newOptions);
        } else {
          AnalyticsService.trackGAEventHandler("filtersAppliedSavedFiltersOff");
          AnalyticsService.trackMx("filtersAppliedSavedFiltersOff", {});

          // Remove only saved filter IDs from selected options
          onOptionsChange(
            selectedOptions.filter((o) => !savedFilterIds.includes(o))
          );
        }
      }
    },
    [selectedOptions, savedFilterIds, onOptionsChange, setIsSavedFiltersApplied]
  );

  // Toggle show more/less for a category
  const toggleCategoryExpand = useCallback((categoryName: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  }, []);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setLiveMessage("All accessibility filters cleared.");

    // Track analytics
    AnalyticsService.trackGAEventHandler("searchAccessibilityFilterCleared");
    AnalyticsService.trackMx("searchAccessibilityFilterCleared", {
      ClearedOptionsCount: selectedOptions.length,
    });

    setSearchTerm("");
    setSelectedCategory(null);
    onClearAll();
  }, [onClearAll, selectedOptions.length]);

  // Check if any option from a category is selected
  const isCategoryActive = useCallback(
    (tags: any[]) => {
      return tags.some((tag: any) => selectedOptions.includes(tag.id));
    },
    [selectedOptions],
  );

  // Handle focus on focusable elements to ensure they scroll into view
  const handleFocusableElementFocus = useCallback((e: React.FocusEvent) => {
    const element = e.target as HTMLElement;
    // For hidden inputs (like in Checkbox with sr-only), scroll the visible parent
    // Otherwise scroll the element itself
    const elementToScroll = element.classList.contains('sr-only') 
      ? element.closest('label') || element.closest('li') || element
      : element;
    
    elementToScroll.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, []);

  // Disable parent page scroll when accessibility panel is open
  useEffect(() => {
    // Save the current overflow values to restore later
    const originalBodyOverflow = document.body.style.overflow;
    const mainElement = document.getElementById('main');
    const originalMainOverflow = mainElement?.style.overflow;

    // Disable scroll on body and main container
    document.body.style.overflow = "hidden";
    if (mainElement) {
      mainElement.style.overflow = "hidden";
    }

    // Cleanup: restore scroll when panel closes
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      if (mainElement) {
        mainElement.style.overflow = originalMainOverflow || "";
      }
    };
  }, []);

  if (!isOpen) return null;
  if (!accessibilityTags || preparedData.length === 0) return null;

  return (
    <FocusTrap
      focusTrapOptions={{
        initialFocus: false,
        allowOutsideClick: true,
        returnFocusOnDeactivate: false,
        escapeDeactivates: false,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}  
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()} // eslint-disable-line jsx-a11y/no-static-element-interactions
        className={cn(
          "bg-bg-brand-secondary-default border border-neutral-100",
          "rounded-xl shadow-lg p-3 w-full md:w-[400px] lg:w-[520px] max-h-[70vh] overflow-hidden flex flex-col",
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={i18n.get("accessibilityFilters")}
      >
        {/* Header */}
        <header className="flex items-center justify-between h-11 px-2 shrink-0">
          <h2 className="title-s-semibold text-fg-neutral-strong">
            {i18n.get("accessibility")}
          </h2>
          <button
            type="button"
            onClick={handleClearAll}
            className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2 px-3 py-1 focus-blue"
            aria-label={`${i18n.get("clearAll")} accessibility filters`}
          >
            {i18n.get("clearAll")}
          </button>
        </header>


        {/* Category Chips */}
        <ScrollContainer className="flex gap-1 py-1 px-0 scrollbar-hide shrink-0 overflow-x-hidden">
        <div className="flex gap-1 py-1 px-0 scrollbar-hide shrink-0 overflow-x-hidden" role="tablist" aria-label="Accessibility categories">
          {preparedData.map((category: any, index: number) => {
            if (category.accessibility_tags.length <= 0) return null;
            const isSelected = selectedCategory === category.name;
            
            // Calculate chip index (excluding categories with no tags)
            const chipIndex = preparedData
              .slice(0, index)
              .filter((cat: any) => cat.accessibility_tags.length > 0)
              .length;
            const totalChips = preparedData.filter((cat: any) => cat.accessibility_tags.length > 0).length;
            
            return (
              <button
                key={index}
                id={`category-tab-${category.name}`}
                ref={(el) => (categoryChipRefs.current[chipIndex] = el)}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-controls={`category-section-${category.name}`}
                onClick={() => handleCategoryClick(category.name)}
                onKeyDown={(e) => handleCategoryChipKeyDown(e, chipIndex, totalChips)}
                className={cn(
                  "flex-shrink-0 min-w-[44px] min-h-[40px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors focus-blue",
                  isSelected
                    ? "label-m-semibold bg-bg-brand-tertiary-default border border-border-token-brand-subtle text-fg-brand-subtle"
                    : "label-m-medium bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover text-fg-neutral-subtle",
                )}
              >
                {category.name}
              </button>
            );
          })}
        </div>
        </ScrollContainer>

        <div className={cn("flex-1", isZoomedIn ? "overflow-y-auto" : "flex flex-col overflow-hidden")}>
          {/* Search Input */}
          <div className="mt-3 shrink-0">
            <div
              className={cn(
                "flex items-center gap-4 min-h-[48px] px-4 py-2",
                "bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg",
              )}
            >
              <input
                ref={searchInputRef}
                type="text"
                placeholder={i18n.get("searchAccessibilityFeatures")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn(
                  "flex-1 min-w-[44px] bg-transparent border-none outline-none focus:ring-0",
                  "text-base md:text-[13px] text-fg-neutral-subtle",
                  "placeholder:text-fg-neutral-subtle",
                )}
                aria-label={i18n.get("searchAccessibilityOptions")}
              />
              <Search
                size={24}
                className="text-fg-neutral-subtle flex-shrink-0"
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Saved Filters Toggle */}
          {isLoggedIn && (
            <div className="shrink-0 px-2 pt-3 ">
              <div className="flex items-center gap-3 py-3 bg-bg-brand-ghost-default">
                <Switch
                  isOn={isSavedFiltersApplied}
                  onChange={(value) => {
                    applySavedFilters(value);
                  }}
                  aria-label={
                    isSavedFiltersApplied
                      ? `${i18n.get("applySavedAccessibilityFilters")} selected`
                      : `${i18n.get("applySavedAccessibilityFilters")}`
                  }
                />
                <div className="label-m-medium text-fg-neutral-strong">
                  {i18n.get("applySavedAccessibilityFilters")}
                </div>
              </div>
            </div>
          )}

          {/* Options List - All categories shown */}
          <div ref={contentRef} className={cn("mt-3", isZoomedIn ? "" : "flex-1 overflow-y-auto")}>
          {filteredCategories.map((category: any, categoryIndex: number) => {
            if (category.accessibility_tags.length <= 0) return null;

            const isExpanded = expandedCategories.has(category.name);
            const tagsToDisplay = isExpanded
              ? category.accessibility_tags
              : category.accessibility_tags.slice(0, 5);

            return (
              <div
                key={categoryIndex}
                ref={(el) => (sectionRefs.current[category.name] = el)}
                id={`category-section-${category.name}`}
                className="mb-4"
                role="tabpanel"
                aria-labelledby={`category-tab-${category.name}`}
              >
                {/* Category Heading */}
                <div
                  className={cn(
                    "p-3 rounded-lg mb-2",
                    isCategoryActive(category.accessibility_tags)
                      ? "bg-container-brand-minimal"
                      : "bg-container-brand-minimal",
                  )}
                >
                  <h3 
                    ref={(el) => (categoryHeadingRefs.current[category.name] = el)}
                    className="text-fg-neutral-strong title-s-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    tabIndex={-1}
                    aria-label={`${category.name} accessibility options`}
                  >
                    {category.name}
                  </h3>
                </div>

                {/* Tags */}
                <ul className="px-2 flex flex-col gap-3">
                  {tagsToDisplay.map((tag: any) => (
                    <li key={tag.id}>
                      <Checkbox
                        checked={selectedOptions.includes(tag.id)}
                        onCheck={() => handleOptionToggle(tag.id, tag.name)}
                        label={tag.name}
                        tabIndex={0}
                        onFocus={handleFocusableElementFocus}
                      />

                      {/* Special handling for tag 428 - show sub-items */}
                      {tag.id === 428 && selectedOptions.includes(tag.id) && (
                        <div className="mt-3 ml-6">
                          <p className="mb-2 text-fg-brand-strong label-m-medium">
                            {i18n.get("wouldAnyOfTheseAlsoWork")}
                          </p>
                          <Checkbox
                            className="mb-2"
                            label={i18n.get("all")}
                            checked={allSubItemsSelected}
                            onCheck={handleAllSubItemsToggle}
                            tabIndex={0}
                            onFocus={handleFocusableElementFocus}
                          />
                          <div className="ml-6 flex flex-col gap-2">
                            {tagsToAdd.map((subTag: any) => (
                              <Checkbox
                                key={subTag.id}
                                label={subTag.name}
                                checked={selectedOptions.includes(subTag.id)}
                                onCheck={() => handleSubItemToggle(subTag.id)}
                                tabIndex={0}
                                onFocus={handleFocusableElementFocus}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Show More/Less Button */}
                {category.accessibility_tags.length > 5 && (
                  <StandardButton
                    onClick={() => toggleCategoryExpand(category.name)}
                    className="ml-2 mt-2"
                    variant="blueLink"
                    size="sm"
                    tabIndex={0}
                    onFocus={handleFocusableElementFocus}
                  >
                    {isExpanded ? i18n.get("showLess") : i18n.get("showMore")}
                  </StandardButton>
                )}
              </div>
            );
          })}

          {/* No results message */}
          {filteredCategories.length === 0 && searchTerm && (
            <p className="text-center py-8 label-m-medium text-fg-neutral-subtle">
              {i18n.get("noResultsFound")}
            </p>
          )}
          </div>
        </div>

        {/* Visually hidden live region for screen reader announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {liveMessage}
        </div>
      </div>
    </FocusTrap>
  );
}

export default AccessibilityFilterPanel;
